import { useState, ChangeEvent } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Grid2 as Grid,
  FormControl,
} from "@mui/material";

import { useAppDispatch } from "@features/common/StoreHook";
import SaveCancelButtonGroup from "@features/common/ButtonGroups/SaveCancelButtonGroup";
import { dialogFormStyle } from "@features/common/styles";
import { useCollection } from "@features/common/collectionHook";
import {
  FormTextField,
  FormTextArea,
  FormDropdownSelect,
} from "@features/common/FormFields";
import { SecretRequest } from "./types";
import {
  createSecret,
  SecretCreateBundle,
  selectSecretsStatus,
} from "./secretsSlice";
import {
  getSelectedSecretResource,
  getSecretResources,
} from "./secretResourcesSlice";

interface SecretDialogProps {
  customerId: string;
  onCancel: () => void;
  show: boolean;
}

// dialog for adding a new secret to Customers keyvault

const validationSchema = yup.object({
  name: yup.string().required(),
  status: yup.string().nullable(),
  description: yup.string().nullable(),
  additional_info: yup.string().nullable(),
  type: yup.string(),
});

export const AddSecretDialog = (props: SecretDialogProps) => {
  const { customerId, onCancel, show } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [secretValue, setSecretValue] = useState<string>("");

  const { handleSubmit, control, reset } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: "",
      status: "",
      description: "",
      type: "",
      additional_info: "",
    },
  });

  const selectedSecretResource = useSelector(getSelectedSecretResource);
  const secretResources = useSelector(getSecretResources);
  const isSubmitting = useSelector(selectSecretsStatus) === "submitting";

  const secretTypes = useCollection("secret-types");

  const handleSecretValueChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSecretValue(event.target.value);
  };

  const handleFormSubmit = (data: any) => {
    const payload: SecretRequest = {
      secret_name: data.name,
      secret_desc: data.description,
      secret_type: data.type,
      secret_value: secretValue ? secretValue : "",
      secret_additional_info: data.additional_info,
    };

    const dataBundle: SecretCreateBundle = {
      customerId: customerId,
      secretResource: selectedSecretResource,
      payload: payload,
    };

    dispatch(createSecret(dataBundle))
      .unwrap()
      .then(() => {
        toast.success(t("Secret added"));
        handleCancel();
      })
      .catch((err) => {
        toast.error(err.message);
      });
  };

  const handleCancel = () => {
    setSecretValue("");
    reset();
    onCancel();
  };

  return (
    <Dialog open={show} fullWidth={true} maxWidth="lg">
      <DialogTitle>{t("label-add-new-secret")}</DialogTitle>
      <DialogContent>
        <FormControl component="fieldset" sx={dialogFormStyle}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                name="secret_resource"
                value={selectedSecretResource}
                fullWidth
                label={t("label-secret-resource")}
                slotProps={{
                  input: {
                    readOnly: true,
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                name="cloud_platform"
                value={
                  secretResources.find(
                    (resource) =>
                      resource.resource_name === selectedSecretResource,
                  )?.cloud_platform || ""
                }
                fullWidth
                label={t("label-cloud-platform")}
                slotProps={{
                  input: {
                    readOnly: true,
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormTextField
                name="name"
                control={control}
                label={t("label-secret-name")}
                readOnly={isSubmitting}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormDropdownSelect
                name="type"
                control={control}
                label={t("label-secret-type")}
                selectItems={secretTypes}
                disabled={isSubmitting}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormTextArea
                name="description"
                control={control}
                label={t("label-secret-description")}
                rows={4}
                readOnly={isSubmitting}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormTextArea
                name="additional_info"
                control={control}
                label={t("label-additional-information")}
                readOnly={isSubmitting}
                rows={4}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                value={secretValue}
                label={t("label-secret-value")}
                onChange={handleSecretValueChange}
                fullWidth
                disabled={isSubmitting}
              />
            </Grid>
          </Grid>
        </FormControl>
        <SaveCancelButtonGroup
          disabled={isSubmitting}
          handleSave={handleSubmit(handleFormSubmit)}
          handleCancel={handleCancel}
        />
      </DialogContent>
    </Dialog>
  );
};
